import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "react-toastify";
import AdminHeader from "../../component/AdminHeader";
import ApiRequest from "../../helper/ApiRequest";
import { ApiEndpoints } from "../../helper/ApiEndpoints";
import { BaseUrl } from "../../helper/BaseUrl";
import GalleryForm, { GALLERY_BATCH_SIZE } from "./GalleryForm";

const absoluteImageUrl = (url) => url ? `${BaseUrl.replace(/\/+$/, "")}${url}` : "";

export default function EditGallery() {
  const navigate = useNavigate();
  const { gallery_id } = useParams();
  const [categories, setCategories] = useState([]);
  const [categoryId, setCategoryId] = useState("");
  const [status, setStatus] = useState("0");
  const [images, setImages] = useState([]);
  const [previews, setPreviews] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    Promise.all([
      ApiRequest("GET", ApiEndpoints.galleryCategory.list),
      ApiRequest("GET", `${ApiEndpoints.gallery.edit}${gallery_id}`),
    ]).then(([categoryResponse, galleryResponse]) => {
      setCategories(categoryResponse.gallery_categories || []);
      const gallery = galleryResponse.gallery;
      setCategoryId(gallery.category_id || "");
      setStatus(gallery.status || "0");
      setImages(gallery.images.map((image) => ({ existing: true, url: image.url })));
      setPreviews(gallery.images.map((image) => absoluteImageUrl(image.url)));
    }).catch((error) => {
      toast.error(error.response?.data?.message || "Unable to load gallery.");
    }).finally(() => setLoading(false));
  }, [gallery_id]);

  const handleImageChange = (index, event) => {
    const file = event.target.files?.[0];
    if (!file) return;
    setImages((current) => current.map((item, itemIndex) => itemIndex === index ? file : item));
    setPreviews((current) => current.map((preview, itemIndex) =>
      itemIndex === index ? URL.createObjectURL(file) : preview
    ));
  };

  const handleAddBatch = () => {
    if (!images.every(Boolean)) return toast.error("Complete the current image set first.");
    setImages((current) => [...current, ...Array(GALLERY_BATCH_SIZE).fill(null)]);
    setPreviews((current) => [...current, ...Array(GALLERY_BATCH_SIZE).fill("")]);
  };

  const handleRemoveBatch = (batchIndex) => {
    const start = batchIndex * GALLERY_BATCH_SIZE;
    previews.slice(start, start + GALLERY_BATCH_SIZE).forEach((preview) => {
      if (preview?.startsWith("blob:")) URL.revokeObjectURL(preview);
    });
    setImages((current) => current.filter((_, index) =>
      index < start || index >= start + GALLERY_BATCH_SIZE
    ));
    setPreviews((current) => current.filter((_, index) =>
      index < start || index >= start + GALLERY_BATCH_SIZE
    ));
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (!categoryId) return toast.error("Please select a gallery category.");
    if (images.length < 7 || images.length % 7 !== 0 || images.some((image) => !image)) {
      return toast.error("Every image set must contain exactly 7 images.");
    }
    const payload = new FormData();
    payload.append("category_id", categoryId);
    payload.append("status", status);
    payload.append("total_images", String(images.length));
    images.forEach((image, index) => {
      if (image instanceof File) {
        payload.append(`image_${index}`, image);
      } else if (image?.existing && image.url) {
        payload.append(`existing_image_${index}`, image.url);
      }
    });
    setSaving(true);
    try {
      const response = await ApiRequest("POST", `${ApiEndpoints.gallery.update}${gallery_id}`, payload);
      toast.success(response.message);
      navigate("/gallery");
    } catch (error) {
      toast.error(error.response?.data?.message || "Unable to update gallery.");
    } finally {
      setSaving(false);
    }
  };

  const pagename = { name: "Gallery Management", user: "Admin", innerpage: "Gallery / Edit" };
  return (
    <>
      <AdminHeader pagename={pagename} />
      <div className="add-content-page">
        {loading ? <div className="add-content-wrap">Loading gallery...</div> : (
          <>
            <GalleryForm formId="galleryEdit" categories={categories} categoryId={categoryId}
              setCategoryId={setCategoryId} status={status} setStatus={setStatus}
              images={images} previews={previews} onImageChange={handleImageChange}
              onAddBatch={handleAddBatch} onRemoveBatch={handleRemoveBatch}
              onSubmit={handleSubmit} saving={saving} />
            <button type="submit" form="galleryEdit" className="default-btn" disabled={saving}>
              {saving ? "Saving..." : "Save"} <span></span>
            </button>
          </>
        )}
      </div>
    </>
  );
}
